/**
 * The HTTP adapter for the Product Cards repository port — the file ADR-001
 * reserved a seat for. The port stays, the providers keep taking a repository
 * prop, no UI file changes; this replaces `localProductCardsRepository` at the
 * route entries once an environment is selected.
 *
 * THE BACKEND'S ADDRESSING MODEL (decided operator-side, 2026-08-18)
 * ------------------------------------------------------------------
 * Every card belongs to one channel and is addressed by `cs_key` in the URL
 * path — the key of the environment picked in the search bar. `party_id` and
 * `industry_id` travel ONLY on create, where the server verifies them against
 * the channel in the graph before the first write and stores them as
 * provenance; no read or write filters on them, so nothing here re-sends them.
 *
 * REVISIONS ARE THIS FILE'S SECRET, NOT THE PORT'S
 * ------------------------------------------------
 * The server guards every write with an integer `revision` (a stale one
 * answers a conflict instead of overwriting another tab's work). The internal
 * `ProductCard` document has no such field — the port predates the API — so
 * this adapter keeps a uid → revision map, filled by every read and bumped by
 * every successful write. A conflict is surfaced as a rejected promise and the
 * map is deliberately NOT updated from the server's answer: adopting the newer
 * revision and letting the autosave retry would overwrite exactly the edit the
 * guard exists to protect.
 *
 * STATUS CODES ARE INVISIBLE HERE
 * -------------------------------
 * `ServiceWrapper` returns the parsed body and never the HTTP status, so
 * outcomes are read from the GMS envelope: `done: true` is success, anything
 * else throws with the server's own message. `errorfound` covers the transport
 * failing outright.
 *
 * ONE REFUSAL IS NOT A FAILURE TO RETRY
 * -------------------------------------
 * Since 2026-09-21 the server refuses a duplicate card name, on create and on
 * rename, with `data.field: "name"`. Retrying that is pointless — the same
 * request fails identically — and the autosave's retry ladder would burn
 * through its delays before reporting something generic. So the field travels
 * out of here on a `ProductCardFieldError` rather than being flattened into a
 * message: callers branch on the TYPE, not on matching the prose.
 */

import AuthManager from 'Common/AuthManager';
import ServiceWrapper from 'Services/ServiceWrapper';
import AppSettings from 'Settings/AppSettings';

import { encodeCard } from '../contract';
import { createDefaultCard } from '../state';
import { isApplicationRef } from '../constants/applications.config';
import {
  CardListingRow,
  CardListPage,
  CardListQuery,
  CardListSortBy,
  ProductCard,
  ProductCardsRepository,
} from '../types';
import { coerceCard } from './parseStoredCards';

/** The channel a repository instance is bound to, plus create's provenance. */
export interface ProductCardsApiScope {
  /** The selected environment's channel key — addresses every request. */
  csKey: string;
  /** Sent on create only; the server verifies both against the channel. */
  partyId: string;
  industryId: string;
}

/**
 * What this adapter needs from a transport verb — a structural subset of
 * `ServiceWrapper`'s `TdoGet`/`TdoPost` (whose parameters are wider), so the
 * real wrapper and a test fake both satisfy it without casts.
 */
type HttpVerb = (
  url: string,
  inputdata: unknown,
  authdata: null,
  token: string | null
) => Promise<{ errorfound?: boolean; data?: unknown }>;

interface HttpService {
  doGet: HttpVerb;
  doPost: HttpVerb;
  doPut: HttpVerb;
  doDelete: HttpVerb;
}

/**
 * Seams for tests: the fixed clock the local adapter's tests already use, and
 * the transport so no test opens a socket. Production callers pass nothing.
 */
interface HttpRepositoryDeps {
  service?: HttpService;
  getToken?: () => string | null;
  apiBaseUrl?: string;
  now?: () => string;
}

/** The UI sort keys → the server's sortable field names (its allow-list). */
const SORT_FIELD_BY_KEY: Record<CardListSortBy, string> = {
  name: 'card.name',
  updatedAt: 'updated_at',
  createdAt: 'created_at',
};

/**
 * The wire envelope. `data` is whatever the route answers with; a listing has
 * `total` and `page` as SIBLINGS of `data` (the backend's convention — do not
 * expect the nested `page: {…}` object some older FE types declare).
 */
interface GmsEnvelope {
  done?: boolean;
  message?: string;
  data?: unknown;
  total?: number;
  page?: number;
}

/**
 * A refusal that names the input responsible, so a caller can render it beside
 * that field and — crucially — knows not to retry it.
 */
export class ProductCardFieldError extends Error {
  readonly field: string;

  constructor(message: string, field: string) {
    super(message);
    // `Error` is special-cased by the TS -> ES5 downlevel: without this the
    // prototype chain is lost and `instanceof` answers false for every caller.
    Object.setPrototypeOf(this, ProductCardFieldError.prototype);
    this.name = 'ProductCardFieldError';
    this.field = field;
  }
}

/** True for the refusals a retry cannot possibly resolve. */
export const isFieldError = (error: unknown): error is ProductCardFieldError =>
  error instanceof ProductCardFieldError;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Server timestamps are epoch SECONDS (the backend rejects millisecond values
 * at its boundary); the internal document wants ISO-8601.
 */
const isoFromEpochSeconds = (value: unknown, fallback: string): string => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return fallback;
  }
  return new Date(value * 1000).toISOString();
};

/** Placeholder for the id the server is about to mint. Never persisted. */
let createCounter = 0;
const nextPlaceholderId = (): string => {
  createCounter += 1;
  return `pending-${createCounter.toString(36)}`;
};

export const createHttpProductCardsRepository = (
  scope: ProductCardsApiScope,
  deps: HttpRepositoryDeps = {}
): ProductCardsRepository => {
  const service = deps.service ?? ServiceWrapper;
  const getToken = deps.getToken ?? ((): string | null => AuthManager.getGmsToken());
  const apiBaseUrl = deps.apiBaseUrl ?? AppSettings.GMS_BACKEND_API_URL ?? '';
  const now = deps.now ?? ((): string => new Date().toISOString());

  const channelUrl = `${apiBaseUrl}product-cards/${encodeURIComponent(scope.csKey)}`;
  const cardUrl = (id: string): string => `${channelUrl}/${encodeURIComponent(id)}`;

  /** uid → last revision this client saw. See the file comment. */
  const revisions = new Map<string, number>();

  /**
   * The envelope's verdict, as a value or a throw. The thrown message is the
   * server's own — it is already written for a person ("This card was changed
   * by someone else. Reload before saving.").
   */
  const unwrap = (
    response: { errorfound?: boolean; data?: unknown },
    failureLabel: string
  ): GmsEnvelope => {
    if (!response || response.errorfound || !isRecord(response.data)) {
      throw new Error(failureLabel);
    }
    const envelope = response.data as GmsEnvelope;
    if (envelope.done !== true) {
      const message =
        typeof envelope.message === 'string' && envelope.message !== ''
          ? envelope.message
          : failureLabel;
      // A refusal the caller can fix by changing one input — today only the
      // duplicate-name rule. It keeps its own type so a caller can tell
      // "type something else" from "the write did not land".
      const field = isRecord(envelope.data) ? envelope.data.field : undefined;
      if (typeof field === 'string' && field !== '') {
        throw new ProductCardFieldError(message, field);
      }
      throw new Error(message);
    }
    return envelope;
  };

  const rememberRevision = (uid: string, revision: unknown): void => {
    if (typeof revision === 'number' && Number.isInteger(revision)) {
      revisions.set(uid, revision);
    }
  };

  /** One server document → one internal card, recording its revision. */
  const cardFromDocument = (document: unknown): ProductCard | null => {
    if (!isRecord(document)) {
      return null;
    }
    const fallbackTimestamp = isoFromEpochSeconds(document.updated_at, now());
    const card = coerceCard(document.card, fallbackTimestamp);
    if (card) {
      rememberRevision(card.id, document.revision);
    }
    return card;
  };

  /**
   * One server LISTING document (the slim projection) → one table row. The
   * full card document is the editor's payload, fetched by get(id). Rows also
   * feed the revision map so a listing-delete presents its revision without a
   * re-read.
   */
  const listingRowFromDocument = (document: unknown): CardListingRow | null => {
    if (!isRecord(document) || typeof document.uid !== 'string') {
      return null;
    }
    const card = isRecord(document.card) ? document.card : {};
    rememberRevision(document.uid, document.revision);
    return {
      id: document.uid,
      name: typeof card.name === 'string' ? card.name : '',
      references: Array.isArray(card.references)
        ? card.references.filter(isApplicationRef)
        : [],
      updatedAt:
        typeof card.updatedAt === 'string'
          ? card.updatedAt
          : isoFromEpochSeconds(document.updated_at, now()),
      createdAt: isoFromEpochSeconds(document.created_at, ''),
      // The display name stamped at write time (Guide's convention); documents
      // from before the stamp fall back to the stable id rather than a blank.
      createdBy:
        typeof document.created_by_name === 'string' && document.created_by_name !== ''
          ? document.created_by_name
          : typeof document.created_by === 'string'
            ? document.created_by
            : '',
    };
  };

  /**
   * The revision a guarded write must present. Reads populate the map before
   * any write can happen in the UI flows (the listing lists, the editor gets),
   * but a miss re-reads rather than guessing: presenting a wrong revision
   * would read as "someone else edited this" to the user, for no reason.
   */
  const revisionFor = async (id: string): Promise<number> => {
    const known = revisions.get(id);
    if (known !== undefined) {
      return known;
    }
    const response = await service.doGet(cardUrl(id), null, null, getToken());
    const envelope = unwrap(response, 'The card could not be loaded.');
    if (isRecord(envelope.data)) {
      rememberRevision(id, envelope.data.revision);
    }
    const refreshed = revisions.get(id);
    if (refreshed === undefined) {
      throw new Error('The card could not be loaded.');
    }
    return refreshed;
  };

  return {
    list: async (query: CardListQuery): Promise<CardListPage> => {
      const params = new URLSearchParams({
        page: String(query.page),
        limit: String(query.limit),
        sort_by: SORT_FIELD_BY_KEY[query.sortBy],
        sort_order: query.sortDir,
      });
      if (query.search !== '') {
        params.set('search', query.search);
      }
      const envelope = unwrap(
        await service.doGet(`${channelUrl}?${params.toString()}`, null, null, getToken()),
        'The card listing could not be loaded.'
      );
      const documents = Array.isArray(envelope.data) ? envelope.data : [];
      const rows: CardListingRow[] = [];
      documents.forEach((document) => {
        const row = listingRowFromDocument(document);
        if (row) {
          rows.push(row);
        }
      });
      return {
        rows,
        total: typeof envelope.total === 'number' ? envelope.total : rows.length,
      };
    },

    get: async (id: string): Promise<ProductCard | null> => {
      const response = await service.doGet(cardUrl(id), null, null, getToken());
      if (!response || response.errorfound || !isRecord(response.data)) {
        throw new Error('The card could not be loaded.');
      }
      const envelope = response.data as GmsEnvelope;
      if (envelope.done !== true) {
        // "Not found" is an answer, not a failure — the editor's own not-found
        // state renders for it (and for another tenant's id, which the server
        // makes indistinguishable on purpose). Only that exact refusal maps to
        // null: an RBAC denial also arrives as `done: false` over HTTP 200, and
        // reporting it as "no such card" would hide an access problem.
        if (envelope.message === 'Product Card Not Found') {
          return null;
        }
        throw new Error(
          typeof envelope.message === 'string' && envelope.message !== ''
            ? envelope.message
            : 'The card could not be loaded.'
        );
      }
      return cardFromDocument(envelope.data);
    },

    create: async (name: string): Promise<ProductCard> => {
      // The document is built client-side — same defaults as the local
      // adapter — and the server mints the id and stamps `card.id` with it.
      const created = createDefaultCard(name, now(), nextPlaceholderId());
      const body = {
        party_id: scope.partyId,
        industry_id: scope.industryId,
        card: encodeCard(created),
      };
      const envelope = unwrap(
        await service.doPost(channelUrl, body, null, getToken()),
        'The card could not be created.'
      );
      const data = isRecord(envelope.data) ? envelope.data : {};
      const uid = typeof data.uid === 'string' ? data.uid : '';
      if (uid === '') {
        throw new Error('The card could not be created.');
      }
      rememberRevision(uid, data.revision);
      return { ...created, id: uid };
    },

    save: async (card: ProductCard): Promise<ProductCard> => {
      const revision = await revisionFor(card.id);
      const body = { card: encodeCard(card), revision };
      const envelope = unwrap(
        await service.doPut(cardUrl(card.id), body, null, getToken()),
        'The card could not be saved.'
      );
      const data = isRecord(envelope.data) ? envelope.data : {};
      rememberRevision(card.id, data.revision);
      return card;
    },

    remove: async (id: string): Promise<void> => {
      const revision = await revisionFor(id);
      unwrap(
        await service.doDelete(cardUrl(id), { revision }, null, getToken()),
        'The card could not be deleted.'
      );
      revisions.delete(id);
    },
  };
};
